import { computed, getCurrentInstance, ref } from '@vue/composition-api';

export const useMemberManagement = () => {
  const vm = getCurrentInstance();
  const store = vm.proxy.$store;

  const errorRequestingPrimarySubscriberDetails = ref(false);
  const memberAddresses = computed(() => {
    return store.getters['memberAddresses'];
  });
  const memberAddressesLoading = computed(() => {
    return store.getters['memberAddressesLoading'];
  });
  const memberAddressesWithExtras = computed(() => {
    return store.getters.memberAddresses.map(address => ({
      ...address,
      ...{
        homePhoneNumber: address.phone,
        cellPhoneNumber: memberDetails.value.member.cellPhoneNumber,
        workPhoneNumber: memberDetails.value.member.workPhoneNumber + (memberDetails.value.member.workPhoneNumberExt ? ` +${memberDetails.value.member.workPhoneNumberExt}` : '')
      }
    }))
  });
  const memberDetails =computed(() => {
    return store.getters['memberDetails'];
  });
  const memberDetailsLoading = computed(() => {
    return store.getters['memberDetailsLoading'];
  });
  const memberDetailsQueryParams = computed(() => {
    return store.getters['memberDetailsQueryParams'];
  });
  const memberEligibility = computed(() => {
    return store.getters['memberEligibility'];
  });
  const memberIcon = computed(() => store.getters['benefitCategories']);
  const memberLocked = computed(() => store.getters['memberLocked']);
  const memberManagementAllowed = computed(() => {
    if (store.getters['isInternalUser']) {
      return store.getters['minimumAccess'].memberMaintenanceAllowed;
    }

    const subGroupAccess = store.getters['subGroupAccess'].find(sg => sg.subGroupCk == memberDetails.value.member.subgroupCk)
    if (!subGroupAccess || !subGroupAccess.access) {
      return false;
    }
    
    return subGroupAccess.access.memberMaintenanceAllowed;
  });
  const memberTransactionDetailsLoading = computed(() => store.getters['memberTransactionDetailsLoading']);
  const memberTransactions = computed(() => store.getters['memberTransactions']);
  const memberTransactionsErrorMessage = computed(() => store.getters['memberTransactionsErrorMessage']);
  const memberTransactionsLoading = computed(() => store.getters['memberTransactionsLoading']);
  const primarySubscriberCkToUseForDependents = computed(() => {
    // the subscriberCk can somehow be different than the memberCk of the subscirber. Until the API is
    // updated, the only way to get this is in the member.relatedMembers[] where memberType == "Subscriber"
    if (
        !memberDetails.value || 
        !memberDetails.value.member || 
        !memberDetails.value.member.relatedMembers || 
        memberDetails.value.member.relatedMembers.length === 0
        ) {
      return null;
    }
    
    const primarySubscriber = memberDetails.value.member.relatedMembers.find(m => m.memberType === 'SUBSCRIBER')
    if (primarySubscriber) {
      return primarySubscriber.memberCk;
    }
    
    console.warn('Could not find memberCk for primary subscriber', memberDetails.value);

    return null;
  });
  const primarySubscriberDetails = computed(() => {
    if (errorRequestingPrimarySubscriberDetails.value || !primarySubscriberCkToUseForDependents.value) {
      return {}
    }

    if (primarySubscriberCkToUseForDependents.value == memberDetails.value.member.memberCk) {
      return memberDetails.value
    }
    
    if (Object.keys(primarySubscriberDetailsInternal.value).length > 0) {
      return primarySubscriberDetailsInternal.value
    }

    if (!requestingPrimarySubscriberDetails.value) {
      requestingPrimarySubscriberDetails.value = true;
      // TODO:  can we call async actions inside computed props?
      new MemberApi({ simulateExample: 'Member Details - Termed Member' }).memberDetails({
        params: {
          memberCk: primarySubscriberCkToUseForDependents.value,
          isEligibility: false,
          isBenefit: false,
          dateOfService: now(),
        }
      })
      .then((res) => {
        primarySubscriberDetailsInternal.value = res;
        })
        .catch(err => {
          errToString(err)
          .then((errStr) => {
            console.warn(errStr);
          })

          errorRequestingPrimarySubscriberDetails.value = true;
        })
        .finally(() => {
          // should this be false?
          requestingPrimarySubscriberDetails.value = true;
        })
    }

    return {}
  });
  const primarySubscriberDetailsInternal = ref({});
  const primarySubscriberVoided  = computed(() => {
    return primarySubscriberDetails.value.member ? primarySubscriberDetails.value.member.memberVoided : false
  });
  const privacyAddress = computed({ 
    get() {
      if (!memberDetails.value || !memberDetails.value.member || !memberDetails.value.member.privacyInfo) {
        return {}
      }
      
      const privacyInfo = memberDetails.value.member.privacyInfo
      if (Object.keys(privacyInfo).length === 0) {
        return {}
      }

      if (privacyInfo.address) {
        return {
          ...privacyInfo,
          ...privacyInfo.address,
          zipCode: privacyInfo.address.zip || privacyInfo.address.zipCode
        }
      }
      
      return privacyInfo
    },
    set(newValue) {
      privacyInfo.value = newValue
    }
  });
  const requestingPrimarySubscriberDetails = ref(false);
  
  return {
    errorRequestingPrimarySubscriberDetails,
    memberAddresses,
    memberAddressesLoading,
    memberAddressesWithExtras,
    memberDetails,
    memberDetailsLoading,
    memberDetailsQueryParams,
    memberEligibility,
    memberIcon,
    memberLocked,
    memberManagementAllowed,
    memberTransactionDetailsLoading,
    memberTransactions,
    memberTransactionsErrorMessage,
    memberTransactionsLoading,
    primarySubscriberCkToUseForDependents,
    primarySubscriberDetails,
    primarySubscriberVoided,
    privacyAddress,
    requestingPrimarySubscriberDetails,
  }
}
